// src/components/dashboard/AssetPopupCard.js
import React from 'react';
import {
  Box,
  Flex,
  HStack,
  VStack,
  Text,
  Badge,
  Divider,
  Progress,
  Spinner,
  Button,
  Icon,
  useColorModeValue
} from '@chakra-ui/react';
import { Popup } from 'react-leaflet';
import { FaBrain, FaCheckCircle } from 'react-icons/fa';
import { MdBuild } from 'react-icons/md';

const AssetPopupCard = ({
  popupKey,
  asset,
  aiMode,
  activeAssetWO,
  isLoadingWO,
  onDetailOpen,
  getAssetColor,
  getAssetReactIcon,
  getStatusConfig,
  getHealthColorScheme
}) => {
  // 1. ALL Hooks must be called at the very top, before any conditional returns
  const headingColor = useColorModeValue('gray.800', 'white');
  const mutedColor = useColorModeValue('gray.500', 'gray.400');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const statBg = useColorModeValue('gray.50', 'gray.800');
  
  // Extracted inline hooks
  const aiBoxBg = useColorModeValue('purple.50', 'purple.900');
  const aiBoxBorder = useColorModeValue('purple.100', 'purple.700');
  const aiTextLabel = useColorModeValue('purple.600', 'purple.300');
  const aiProgressBg = useColorModeValue('purple.200', 'purple.800');
  const activeWoTitleColor = useColorModeValue('blue.600', 'blue.300');

  // 2. Early return comes AFTER all hooks
  if (!asset) return null;

  const statusCfg = getStatusConfig(asset.status);

  return (
    <Popup
      key={`popup-${popupKey}`} // Forces Leaflet to open it dynamically
      position={[asset.latitude, asset.longitude]}
      autoPan={false} // Prevents camera collision with flyTo logic
      maxWidth={340}
      minWidth={280}
      className="custom-asset-popup"
    >
      <Box p={2}>
        {/* ── HEADER: Icon, Name, Type, Status ── */}
        <HStack justify="space-between" align="flex-start" mb={3}>
          <HStack spacing={3}>
            <Flex
              w="36px"
              h="36px"
              borderRadius="md"
              bg={`${getAssetColor(asset.type)}20`}
              align="center"
              justify="center"
              flexShrink={0}
            >
              <Icon as={getAssetReactIcon(asset.type)} color={getAssetColor(asset.type)} boxSize={4} />
            </Flex>
            <VStack align="start" spacing={0}>
              <Text fontWeight="800" fontSize="sm" color={headingColor} noOfLines={1}>
                {asset.name}
              </Text>
              <Text fontSize="xs" color={mutedColor} textTransform="capitalize">
                {asset.type} • {String(asset.id).split('-')[0]}
              </Text>
            </VStack>
          </HStack>
          <Badge
            colorScheme={statusCfg.color}
            fontSize="2xs"
            px={2}
            py={0.5}
            borderRadius="full"
          >
            {statusCfg.label}
          </Badge>
        </HStack>

        <Divider mb={3} borderColor={borderColor} />

        {/* ── AI HEALTH WIDGET (only in AI Lens mode) ── */}
        {aiMode && asset.health_score != null && (
          <Box mb={3} p={2} bg={aiBoxBg} borderRadius="md" borderWidth="1px" borderColor={aiBoxBorder}>
            <HStack justify="space-between" mb={1}>
              <HStack spacing={1}>
                <Icon as={FaBrain} color="purple.500" boxSize={3} />
                <Text fontSize="2xs" fontWeight="bold" color={aiTextLabel}>AI HEALTH SCORE</Text>
              </HStack>
              <Badge colorScheme={getHealthColorScheme(asset.health_score)} fontSize="2xs">
                {asset.health_score}%
              </Badge>
            </HStack>
            <Progress
              value={asset.health_score}
              size="xs"
              colorScheme={getHealthColorScheme(asset.health_score)}
              borderRadius="full"
              bg={aiProgressBg}
            />
          </Box>
        )}

        {/* ── CURRENT WORK ORDER WIDGET ── */}
        <Box mb={3} p={2} bg={statBg} borderRadius="md" borderWidth="1px" borderColor={borderColor}>
          <Text fontSize="2xs" fontWeight="bold" color={mutedColor} mb={1} textTransform="uppercase">
            Current Work Order
          </Text>
          {isLoadingWO ? (
            <HStack py={1}>
              <Spinner size="xs" color="blue.500" />
              <Text fontSize="xs" color={mutedColor}>Checking database...</Text>
            </HStack>
          ) : activeAssetWO ? (
            <VStack align="start" spacing={1}>
              <Text fontSize="sm" fontWeight="700" color={activeWoTitleColor} noOfLines={1}>
                {activeAssetWO.title}
              </Text>
              <HStack w="full" justify="space-between">
                <Badge size="xs" colorScheme="orange" variant="subtle" textTransform="capitalize">
                  {activeAssetWO.priority} priority
                </Badge>
                <Text fontSize="2xs" color={mutedColor} fontWeight="600" textTransform="uppercase">
                  {String(activeAssetWO.status).replace('_', ' ')}
                </Text>
              </HStack>
            </VStack>
          ) : (
            <HStack py={1} spacing={2}>
              <Icon as={FaCheckCircle} color="green.400" boxSize={3} />
              <Text fontSize="xs" color={mutedColor} fontStyle="italic">No active issues reported.</Text>
            </HStack>
          )}
        </Box>

        {/* ── OPEN FULL DRAWER BUTTON ── */}
        <Button
          w="full"
          size="sm"
          colorScheme="blue"
          variant="outline"
          leftIcon={<Icon as={MdBuild} />}
          onClick={(e) => {
            e.stopPropagation();
            onDetailOpen(); // Opens the Asset Command Drawer
          }}
        >
          View Asset Details
        </Button>
      </Box>
    </Popup>
  );
};

export default AssetPopupCard;